// Recognise social and media links from the URL alone, with no network.
//
// Instagram, TikTok, Facebook and LinkedIn routinely block scraping, so the
// metadata fetch can fail or come back as a login page. The URL still says
// which platform it is, what kind of post, and often who posted it. That is
// enough to build a useful card, and to know whether a post can be pulled
// in full with Top Creatives.
//
// Pure TypeScript with no imports: used by the fetch-link-metadata edge
// function (Deno) and by the app (src/lib/linkPreview.ts) alike.

export type SocialPlatform =
  | 'instagram'
  | 'tiktok'
  | 'youtube'
  | 'x'
  | 'facebook'
  | 'linkedin'
  | 'pinterest'
  | 'threads'
  | 'reddit'
  | 'vimeo';

export type SocialKind = 'post' | 'reel' | 'video' | 'short' | 'story' | 'pin' | 'article' | 'profile' | 'board' | 'other';

/** Same union as LinkContent.contentType in src/components/items/LinkCard.tsx. */
export type LinkContentType = 'product' | 'article' | 'tweet' | 'instagram_reel' | 'video' | 'website' | 'social_media';

export interface SocialLinkInfo {
  platform: SocialPlatform;
  /** Display name, e.g. "Instagram", "X". */
  platformName: string;
  kind: SocialKind;
  /** Account handle without the @, when the URL carries it. */
  handle?: string;
  /** Post, video or pin id, when the URL carries it. */
  postId?: string;
  /** True for one post, reel, video or pin; false for profiles, boards and the like. */
  isPost: boolean;
  /** Card title when the page itself gives nothing better. */
  title: string;
  favicon: string;
  contentType: LinkContentType;
}

const PLATFORM_NAMES: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  threads: 'Threads',
  reddit: 'Reddit',
  vimeo: 'Vimeo',
};

const FAVICONS: Record<SocialPlatform, string> = {
  instagram: 'https://www.instagram.com/favicon.ico',
  tiktok: 'https://www.tiktok.com/favicon.ico',
  youtube: 'https://www.youtube.com/favicon.ico',
  x: 'https://abs.twimg.com/favicons/twitter.3.ico',
  facebook: 'https://www.facebook.com/favicon.ico',
  linkedin: 'https://www.linkedin.com/favicon.ico',
  pinterest: 'https://www.pinterest.com/favicon.ico',
  threads: 'https://www.threads.net/favicon.ico',
  reddit: 'https://www.reddit.com/favicon.ico',
  vimeo: 'https://vimeo.com/favicon.ico',
};

const KIND_NOUNS: Record<SocialKind, string> = {
  post: 'post',
  reel: 'reel',
  video: 'video',
  short: 'Short',
  story: 'story',
  pin: 'pin',
  article: 'article',
  profile: 'profile',
  board: 'board',
  other: 'link',
};

/** Host is `domain` or a subdomain of it. `x.com` must not match `netflix.com`. */
function hostIs(host: string, ...domains: string[]): boolean {
  return domains.some((d) => host === d || host.endsWith(`.${d}`));
}

function platformForHost(host: string): SocialPlatform | null {
  if (hostIs(host, 'instagram.com', 'instagr.am', 'ig.me')) return 'instagram';
  if (hostIs(host, 'tiktok.com')) return 'tiktok';
  if (hostIs(host, 'youtube.com', 'youtu.be', 'youtube-nocookie.com')) return 'youtube';
  if (hostIs(host, 'x.com', 'twitter.com', 'fxtwitter.com', 'vxtwitter.com')) return 'x';
  if (hostIs(host, 'facebook.com', 'fb.com', 'fb.watch', 'fb.me')) return 'facebook';
  if (hostIs(host, 'linkedin.com', 'lnkd.in')) return 'linkedin';
  if (hostIs(host, 'pin.it') || /(^|\.)pinterest\.[a-z.]+$/.test(host)) return 'pinterest';
  if (hostIs(host, 'threads.net', 'threads.com')) return 'threads';
  if (hostIs(host, 'reddit.com', 'redd.it')) return 'reddit';
  if (hostIs(host, 'vimeo.com')) return 'vimeo';
  return null;
}

/** Path segments that are app routes, not account handles. */
const RESERVED = new Set([
  'p', 'reel', 'reels', 'tv', 'stories', 'explore', 'accounts', 'direct', 'share', 's',
  'i', 'home', 'search', 'intent', 'hashtag', 'settings', 'messages', 'notifications', 'compose',
  'watch', 'groups', 'events', 'pages', 'photo', 'photos', 'videos', 'gaming', 'marketplace',
  'permalink.php', 'story.php', 'profile.php', 'login', 'login.php', 'feed', 'pin', 'ideas', 'today',
]);

function cleanHandle(value: string | undefined): string | undefined {
  if (!value) return undefined;
  let handle = value;
  try {
    handle = decodeURIComponent(value);
  } catch {
    // keep the raw segment
  }
  handle = handle.replace(/^@/, '').trim();
  if (!handle || RESERVED.has(handle.toLowerCase())) return undefined;
  if (!/^[\p{L}\p{N}._-]{1,100}$/u.test(handle)) return undefined;
  return handle;
}

interface Parsed {
  kind: SocialKind;
  handle?: string;
  postId?: string;
}

function parseInstagram(segs: string[]): Parsed {
  // /p/<code>, /reel/<code>, /tv/<code>, and the newer /<handle>/p/<code>
  const postIdx = segs.findIndex((s) => ['p', 'reel', 'reels', 'tv'].includes(s.toLowerCase()));
  if (postIdx >= 0 && segs[postIdx + 1]) {
    const marker = segs[postIdx].toLowerCase();
    const kind: SocialKind = marker === 'p' ? 'post' : marker === 'tv' ? 'video' : 'reel';
    return { kind, postId: segs[postIdx + 1], handle: postIdx > 0 ? cleanHandle(segs[postIdx - 1]) : undefined };
  }
  if (segs[0]?.toLowerCase() === 'stories' && segs[1]) {
    return { kind: 'story', handle: cleanHandle(segs[1]), postId: segs[2] };
  }
  const handle = cleanHandle(segs[0]);
  return handle && segs.length <= 2 ? { kind: 'profile', handle } : { kind: 'other' };
}

function parseTikTok(host: string, segs: string[]): Parsed {
  // vm.tiktok.com/<code>, vt.tiktok.com/<code>, tiktok.com/t/<code>: short links to a video
  if ((host.startsWith('vm.') || host.startsWith('vt.')) && segs[0]) return { kind: 'video' };
  if (segs[0] === 't' && segs[1]) return { kind: 'video' };
  const handle = segs[0]?.startsWith('@') ? cleanHandle(segs[0]) : undefined;
  if ((segs[1] === 'video' || segs[1] === 'photo') && segs[2]) {
    return { kind: segs[1] === 'video' ? 'video' : 'post', handle, postId: segs[2] };
  }
  if (segs[0] === 'embed' && segs[1]) return { kind: 'video', postId: segs[segs.length - 1] };
  return handle ? { kind: 'profile', handle } : { kind: 'other' };
}

function parseYouTube(url: URL, host: string, segs: string[]): Parsed {
  if (hostIs(host, 'youtu.be') && segs[0]) return { kind: 'video', postId: segs[0] };
  if (segs[0] === 'watch' && url.searchParams.get('v')) return { kind: 'video', postId: url.searchParams.get('v')! };
  if (segs[0] === 'shorts' && segs[1]) return { kind: 'short', postId: segs[1] };
  if ((segs[0] === 'live' || segs[0] === 'embed' || segs[0] === 'v') && segs[1]) return { kind: 'video', postId: segs[1] };
  if (segs[0]?.startsWith('@')) return { kind: 'profile', handle: cleanHandle(segs[0]) };
  if ((segs[0] === 'c' || segs[0] === 'user') && segs[1]) return { kind: 'profile', handle: cleanHandle(segs[1]) };
  return { kind: 'other' };
}

function parseX(segs: string[]): Parsed {
  // /<handle>/status/<id>, /i/status/<id>, /i/web/status/<id>
  const statusIdx = segs.findIndex((s) => s === 'status' || s === 'statuses');
  if (statusIdx >= 0 && segs[statusIdx + 1]) {
    return { kind: 'post', postId: segs[statusIdx + 1], handle: statusIdx > 0 ? cleanHandle(segs[statusIdx - 1]) : undefined };
  }
  const handle = cleanHandle(segs[0]);
  return handle && segs.length === 1 ? { kind: 'profile', handle } : { kind: 'other' };
}

function parseFacebook(url: URL, host: string, segs: string[]): Parsed {
  if (hostIs(host, 'fb.watch')) return { kind: 'video' };
  const first = segs[0]?.toLowerCase();
  if (first === 'share') {
    // /share/p/<id>, /share/r/<id>, /share/v/<id>
    const t = segs[1]?.toLowerCase();
    if (t === 'r') return { kind: 'reel', postId: segs[2] };
    if (t === 'v') return { kind: 'video', postId: segs[2] };
    return { kind: 'post', postId: segs[2] };
  }
  if (first === 'reel' && segs[1]) return { kind: 'reel', postId: segs[1] };
  if (first === 'watch') return { kind: 'video', postId: url.searchParams.get('v') ?? undefined };
  if (first === 'permalink.php' || first === 'story.php') {
    return { kind: 'post', postId: url.searchParams.get('story_fbid') ?? undefined };
  }
  if (first === 'groups' && segs[2] === 'posts') return { kind: 'post', postId: segs[3] };
  const handle = cleanHandle(segs[0]);
  if (segs[1] === 'posts' && segs[2]) return { kind: 'post', handle, postId: segs[2] };
  if (segs[1] === 'videos' && segs[2]) return { kind: 'video', handle, postId: segs[segs.length - 1] };
  if (segs[1] === 'reels' && segs[2]) return { kind: 'reel', handle, postId: segs[2] };
  return handle && segs.length === 1 ? { kind: 'profile', handle } : { kind: 'other' };
}

function parseLinkedIn(host: string, segs: string[]): Parsed {
  if (hostIs(host, 'lnkd.in')) return { kind: 'other' };
  const first = segs[0]?.toLowerCase();
  if (first === 'posts' && segs[1]) {
    // /posts/<handle>_<title-words>-activity-<id>-<hash>
    const slug = segs[1];
    const underscore = slug.indexOf('_');
    const handle = underscore > 0 ? cleanHandle(slug.slice(0, underscore)) : undefined;
    const id = slug.match(/activity-(\d+)/)?.[1];
    return { kind: 'post', handle, postId: id };
  }
  if (first === 'feed' && segs[1] === 'update' && segs[2]) {
    return { kind: 'post', postId: segs[2].match(/(\d{6,})/)?.[1] };
  }
  if (first === 'pulse' && segs[1]) return { kind: 'article' };
  if ((first === 'in' || first === 'company' || first === 'school') && segs[1]) {
    return { kind: 'profile', handle: cleanHandle(segs[1]) };
  }
  return { kind: 'other' };
}

function parsePinterest(host: string, segs: string[]): Parsed {
  if (hostIs(host, 'pin.it')) return { kind: 'pin' };
  if (segs[0] === 'pin' && segs[1]) return { kind: 'pin', postId: segs[1] };
  const handle = cleanHandle(segs[0]);
  if (handle && segs.length >= 2) return { kind: 'board', handle };
  return handle ? { kind: 'profile', handle } : { kind: 'other' };
}

function parseThreads(segs: string[]): Parsed {
  const handle = segs[0]?.startsWith('@') ? cleanHandle(segs[0]) : undefined;
  if (segs[1] === 'post' && segs[2]) return { kind: 'post', handle, postId: segs[2] };
  return handle ? { kind: 'profile', handle } : { kind: 'other' };
}

function parseReddit(host: string, segs: string[]): Parsed {
  if (hostIs(host, 'redd.it')) return { kind: 'post', postId: segs[0] };
  const commentsIdx = segs.indexOf('comments');
  if (commentsIdx >= 0 && segs[commentsIdx + 1]) return { kind: 'post', postId: segs[commentsIdx + 1] };
  if ((segs[0] === 'u' || segs[0] === 'user') && segs[1]) return { kind: 'profile', handle: cleanHandle(segs[1]) };
  return { kind: 'other' };
}

function parseVimeo(segs: string[]): Parsed {
  const id = segs.find((s) => /^\d+$/.test(s));
  return id ? { kind: 'video', postId: id } : { kind: 'other' };
}

function buildTitle(platform: SocialPlatform, p: Parsed): string {
  const name = PLATFORM_NAMES[platform];
  // Platforms whose accounts are written with an @
  const at = platform === 'linkedin' || platform === 'facebook' ? '' : '@';
  if (p.kind === 'profile' || p.kind === 'board') {
    if (!p.handle) return name;
    return p.kind === 'board' ? `Pinterest board by @${p.handle}` : `${at}${p.handle} on ${name}`;
  }
  if (p.kind === 'other') return name;
  const noun = platform === 'youtube' && p.kind === 'video' ? 'video' : KIND_NOUNS[p.kind];
  const base = `${name} ${noun}`;
  return p.handle ? `${base} by ${at}${p.handle}` : base;
}

function contentTypeFor(platform: SocialPlatform, p: Parsed): LinkContentType {
  if (platform === 'instagram' && p.kind === 'reel') return 'instagram_reel';
  if (platform === 'x' && p.kind === 'post') return 'tweet';
  if (platform === 'youtube' || platform === 'vimeo') return p.kind === 'profile' ? 'social_media' : 'video';
  if (platform === 'tiktok' && p.kind === 'video') return 'video';
  if (platform === 'linkedin' && p.kind === 'article') return 'article';
  return 'social_media';
}

/** Recognise a social or media URL. Returns null for anything else, or for a string that is not a URL. */
export function parseSocialUrl(rawUrl: string): SocialLinkInfo | null {
  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const host = url.hostname.toLowerCase().replace(/^www\.|^m\.|^mobile\./, '');
  const platform = platformForHost(host);
  if (!platform) return null;

  const segs = url.pathname.split('/').filter(Boolean);
  let parsed: Parsed;
  switch (platform) {
    case 'instagram': parsed = parseInstagram(segs); break;
    case 'tiktok': parsed = parseTikTok(host, segs); break;
    case 'youtube': parsed = parseYouTube(url, host, segs); break;
    case 'x': parsed = parseX(segs); break;
    case 'facebook': parsed = parseFacebook(url, host, segs); break;
    case 'linkedin': parsed = parseLinkedIn(host, segs); break;
    case 'pinterest': parsed = parsePinterest(host, segs); break;
    case 'threads': parsed = parseThreads(segs); break;
    case 'reddit': parsed = parseReddit(host, segs); break;
    case 'vimeo': parsed = parseVimeo(segs); break;
  }

  const isPost = !['profile', 'board', 'other'].includes(parsed.kind);
  return {
    platform,
    platformName: PLATFORM_NAMES[platform],
    kind: parsed.kind,
    handle: parsed.handle,
    postId: parsed.postId,
    isPost,
    title: buildTitle(platform, parsed),
    favicon: FAVICONS[platform],
    contentType: contentTypeFor(platform, parsed),
  };
}

/**
 * Titles a blocked or logged-out page tends to return instead of the post:
 * "Instagram", "Login • Instagram", "TikTok - Make Your Day", "X". Treat these
 * as no title so the URL-derived one wins.
 */
export function isPlaceholderTitle(title: string | null | undefined, info: SocialLinkInfo | null): boolean {
  const t = (title ?? '').trim().toLowerCase();
  if (!t) return true;
  if (/^(log ?in|sign ?in|sign up|access denied|error|page not found|just a moment|attention required)\b/.test(t)) return true;
  if (/\b(log ?in|sign ?in)\b.*[•|·-]/.test(t)) return true;
  if (!info) return false;
  const name = info.platformName.toLowerCase();
  const generic = [
    name,
    'twitter',
    'instagram',
    'facebook',
    'linkedin',
    'pinterest',
    'youtube',
  ];
  return generic.some((g) => t === g || t === `${g}.com` || t.startsWith(`${g} - `) || t.startsWith(`${g} | `));
}


/**
 * Platforms Top Creatives can pull a single post from. Keep in sync with
 * SINGLE_POST_ACTORS in supabase/functions/apify-scrape/index.ts.
 */
export const PULLABLE_PLATFORMS: SocialPlatform[] = ['instagram', 'tiktok', 'facebook'];

/** One post, reel or video Top Creatives can pull in full. Stories expire, so no. */
export function isPullablePost(info: SocialLinkInfo | null): info is SocialLinkInfo {
  return !!info && info.isPost && info.kind !== 'story' && PULLABLE_PLATFORMS.includes(info.platform);
}
